import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const BRANDS = {
  jet:          { label: 'JET',          color: '#FF6B00', bg: 'from-orange-500 to-orange-700', tagline: 'Stationsmanagement' },
  shell:        { label: 'Shell',        color: '#FFCC00', bg: 'from-yellow-400 to-red-500',    tagline: 'Stationsmanagement' },
  aral:         { label: 'Aral',         color: '#00539F', bg: 'from-blue-600 to-blue-900',     tagline: 'Stationsmanagement' },
  esso:         { label: 'Esso',         color: '#CC0000', bg: 'from-red-600 to-red-900',       tagline: 'Stationsmanagement' },
  totalenergies:{ label: 'TotalEnergies',color: '#EF3E33', bg: 'from-red-500 to-yellow-500',   tagline: 'Stationsmanagement' },
  omv:          { label: 'OMV',          color: '#003087', bg: 'from-blue-800 to-blue-950',     tagline: 'Stationsmanagement' },
  star:         { label: 'Star / ORLEN', color: '#ED1B24', bg: 'from-rose-600 to-rose-900',     tagline: 'Stationsmanagement' },
  westfalen:    { label: 'Westfalen',    color: '#002B5C', bg: 'from-blue-900 to-red-700',      tagline: 'Stationsmanagement' },
  agip:         { label: 'Agip / Eni',   color: '#E30613', bg: 'from-red-600 to-yellow-600',    tagline: 'Stationsmanagement' },
  q1:           { label: 'Q1',           color: '#E30613', bg: 'from-red-700 to-red-950',       tagline: 'Stationsmanagement' },
  raiffeisen:   { label: 'Raiffeisen',   color: '#005CA9', bg: 'from-blue-600 to-yellow-600',   tagline: 'Stationsmanagement' },
  bft:          { label: 'bft',          color: '#003087', bg: 'from-blue-800 to-blue-950',     tagline: 'Stationsmanagement' },
  sprint:       { label: 'Sprint',       color: '#FF6600', bg: 'from-orange-500 to-blue-900',   tagline: 'Stationsmanagement' },
  ran:          { label: 'RAN',          color: '#E30613', bg: 'from-rose-600 to-rose-900',     tagline: 'Stationsmanagement' },
  hem:          { label: 'HEM',          color: '#009640', bg: 'from-green-600 to-green-900',   tagline: 'Stationsmanagement' },
  avia:         { label: 'AVIA',         color: '#E8001A', bg: 'from-red-700 to-blue-900',      tagline: 'Stationsmanagement' },
};

export default function DemoLanding() {
  const { brand: urlBrand } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedBrand, setSelectedBrand] = useState(urlBrand || '');
  const [restoring, setRestoring] = useState(true);

  const clearDemoStorage = () => {
    ['demo_access_token', 'demo_expires_at', 'demo_brand', 'demo_session_id'].forEach(k => localStorage.removeItem(k));
  };

  // Session-Wiederherstellung
  useEffect(() => {
    const savedToken = localStorage.getItem('demo_access_token');
    const savedExpires = localStorage.getItem('demo_expires_at');
    if (savedToken && savedExpires && new Date(savedExpires) > new Date()) {
      axios.get(`${API}/api/demo/validate`, {
        headers: { Authorization: `Bearer ${savedToken}` }
      }).then(() => {
        localStorage.setItem('token', savedToken);
        window.location.href = '/admin/dashboard';
      }).catch(() => {
        clearDemoStorage();
        setRestoring(false);
      });
    } else {
      clearDemoStorage();
      setRestoring(false);
    }
    if (urlBrand && BRANDS[urlBrand]) setSelectedBrand(urlBrand);
  }, []);

  const startDemo = async (brand) => {
    if (!token) { setError('Demo-Link ungültig: Token fehlt.'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/api/demo/start`, { brand, token });
      const { access_token, session_id, expires_at, brand: b } = res.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('demo_access_token', access_token);
      localStorage.setItem('demo_expires_at', expires_at);
      localStorage.setItem('demo_brand', b);
      localStorage.setItem('demo_session_id', session_id);
      localStorage.setItem('is_demo', 'true');
      // Full page reload so AuthContext picks up the new token from localStorage
      window.location.href = '/admin/dashboard';
    } catch (err) {
      setError(err.response?.data?.detail || 'Demo konnte nicht gestartet werden.');
      setLoading(false);
    }
  };

  if (restoring) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
      </div>
    );
  }

  const active = selectedBrand ? BRANDS[selectedBrand] : null;

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center px-4 py-12">
      <div className="text-center mb-10 max-w-3xl">
        <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-1.5 rounded-full text-white/70 text-sm mb-6">
          <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse inline-block" />
          Live Demo – Keine Registrierung
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3">AllFlowX Demo</h1>
        <p className="text-gray-400 text-base">Wähle deine Marke und starte sofort mit dem vollständigen System.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full max-w-3xl mb-8">
        {Object.entries(BRANDS).map(([key, brand]) => (
          <button
            key={key}
            onClick={() => setSelectedBrand(key)}
            data-testid={`brand-${key}`}
            className={`relative overflow-hidden rounded-2xl p-5 text-left transition-all border-2 bg-gradient-to-br ${brand.bg}
              ${selectedBrand === key ? 'border-white shadow-xl scale-[1.02]' : 'border-white/10 hover:border-white/30'}`}
          >
            <div className="text-white font-bold text-xl mb-1">{brand.label}</div>
            <div className="text-white/60 text-xs">{brand.tagline}</div>
            {selectedBrand === key && (
              <div className="absolute top-3 right-3 w-5 h-5 bg-white rounded-full flex items-center justify-center">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: brand.color }} />
              </div>
            )}
          </button>
        ))}
      </div>

      <button
        onClick={() => active && startDemo(selectedBrand)}
        disabled={!active || loading}
        data-testid="start-demo-btn"
        className={`w-full max-w-3xl py-4 rounded-2xl font-bold text-lg flex items-center justify-center gap-2 transition-all
          ${active && !loading ? 'bg-white text-gray-900 hover:bg-gray-100 shadow-xl' : 'bg-white/10 text-white/30 cursor-not-allowed'}`}
      >
        {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Demo wird gestartet...</> :
          active ? `Demo als ${active.label} starten` : 'Marke auswählen'}
      </button>

      {error && (
        <div className="mt-4 bg-red-900/40 border border-red-500 rounded-xl px-4 py-3 text-red-300 text-sm max-w-lg w-full">
          {error}
        </div>
      )}

      <div className="mt-8 flex flex-wrap justify-center gap-4 text-gray-600 text-xs">
        <span>Session: 4 Stunden</span><span>•</span>
        <span>Alle Module freigeschaltet</span><span>•</span>
        <span>Daten werden automatisch gelöscht</span>
      </div>
    </div>
  );
}
